/**
 * Zod schemas for kanban status configuration on a hub.
 *
 * Single source of truth for cross-field validation invariants enforced on
 * every write path that lands `kanban_statuses` in HubDO SQLite:
 *   - REST: createHubBody / updateHubBody (workers/shared/src/contracts/setup/hubs.ts)
 *   - CI:   hubAsCodeConfigSchema         (workers/shared/src/contracts/ci.ts)
 *
 * Field shapes mirror the existing TypeScript interfaces:
 *   - HubAsCodeKanbanStatus / HubAsCodeKanbanFollowup (contracts/ci.ts)
 *   - KanbanStatusConfig    / FollowupConfig         (backend/scheduling/types.ts)
 *
 * Human-facing constraint summary lives in skills/wayai/SKILL.md "Kanban &
 * States — Constraints".
 *
 * `additional_context_schema` is a JSON Schema (Draft 2020-12). Structural
 * shape is gated here (size, reserved-name); semantic well-formedness
 * (`ajv.validateSchema`) runs at the backend route handler so AJV does not
 * leak into shared/frontend bundles.
 */

import { z } from 'zod';
import { withinJsonLength } from '../common';
import { slugSchema, visibleLength } from './slug';

// Defensive bounds. Typical hubs have 3–10 statuses with 0–3 followups each;
// caps are generous on intent and tight enough that the per-request
// superRefine cost stays bounded. Mirrors MAX_GRANTS in contracts/tokens.ts.
const MAX_KANBAN_STATUSES = 50;
const MAX_FOLLOWUPS_PER_STATUS = 20;
// Lanes group statuses, so #lanes ≤ #statuses in practice; this bound is generous.
const MAX_LANES = 20;
export const MAX_ADDITIONAL_CONTEXT_SCHEMA_BYTES = 16384;

/**
 * Reserved placeholder name in `additional_instructions` templates: dumps the
 * full payload as JSON. Schemas may not declare this as a top-level property.
 */
export const RESERVED_TEMPLATE_DUMP_NAME = 'additional_data';

/** Matches `{{path}}` and `{{ path.to.field }}` in templates. */
const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g;

/**
 * Kanban slug — alias of the shared slug schema. Used as the immutable runtime
 * identifier (`conversation.kanban_status`, ClickHouse `data.meta.kanban_status`,
 * native tool params, transition refs).
 */
export const kanbanStatusSlugSchema = slugSchema;

// =============================================================================
// FOLLOWUP SCHEMA
// =============================================================================

/**
 * What a followup counts its delay from. Named rather than inlined because the
 * same union is mirrored by the CI contract, the backend scheduling config, the
 * persisted `conversation_schedule.followup_type` column and the web editor —
 * a union spelled five times drifts.
 *
 * - `inactivity` — silence since the contact's last message.
 * - `before_event` — counts BACK to `scheduled_event_date`.
 * - `inactivity_after_event` — silence since the event instant.
 * - `inactivity_after_before_event` — silence since a specific linked
 *   `before_event` fired, named by `after_followup_id`.
 */
export const followupTypeSchema = z.enum([
  'inactivity',
  'before_event',
  'inactivity_after_event',
  'inactivity_after_before_event',
]);
export type FollowupType = z.infer<typeof followupTypeSchema>;

/**
 * Followup types whose delay is measured against `scheduled_event_date`, and
 * which therefore only make sense on a scheduling status.
 */
export const EVENT_RELATIVE_FOLLOWUP_TYPES = [
  'before_event',
  'inactivity_after_event',
  'inactivity_after_before_event',
] as const satisfies readonly FollowupType[];

/**
 * Membership test for the set above. Exported because the three consumers live
 * in three packages (contract, native tool, web editor) and each would
 * otherwise repeat the same unchecked `as readonly string[]` cast, which
 * silently swallows a mistyped value.
 */
export function isEventRelativeFollowupType(type: unknown): boolean {
  return typeof type === 'string' && (EVENT_RELATIVE_FOLLOWUP_TYPES as readonly string[]).includes(type);
}

/** Delay units a followup threshold may use. Named for the same reason the type union is. */
export const followupTimeUnitSchema = z.enum(['seconds', 'minutes', 'hours', 'days']);

/**
 * A followup identifier: non-empty and free of surrounding whitespace.
 *
 * REJECTS a padded value rather than trimming it. `.trim()` is a Zod
 * *transform*, so it changes the parsed output — and only the REST path
 * persists that output. The CI/CLI path validates with
 * `kanbanStatusesArraySchema.safeParse` purely to re-path issues, discards
 * `result.data`, and persists the raw YAML, so a trimming schema would accept
 * `after_followup_id: " reminder"` against `id: reminder` and then store the
 * padded value. At runtime the scan compares `f.after_followup_id === id`
 * against what was stored, finds no match, and the chase silently never arms
 * with nothing logged. Refusing the input keeps validation and persistence
 * describing the same value on every write path.
 */
const followupIdSchema = z
  .string()
  .min(1, 'must not be empty')
  .refine((v) => v === v.trim(), 'must not have leading or trailing whitespace');

/**
 * The types an author may actually pick, for editors that render a picker.
 * Derived from the enum rather than hand-listed, so an editor can never offer a
 * value the contract refuses nor hide one it accepts.
 */
export const SELECTABLE_FOLLOWUP_TYPES: readonly FollowupType[] = followupTypeSchema.options;

/**
 * Per-status cap on how many distinct `before_event` followups may be link
 * targets.
 *
 * This bounds the reply path's fan-out, not the config's size. Every inbound
 * message rebuilds one linked chain per distinct live `after_followup_id`, so
 * K link targets turn a single message into K schedule inserts and K
 * `SCHEDULE_INDEX` puts where an unlinked hub does exactly one of each. Nothing
 * downstream bounds K — it is whatever the author wrote — so it is bounded
 * here, at author time.
 */
export const MAX_LINKED_FOLLOWUP_TARGETS_PER_STATUS = 5;

export const followupSchema = z
  .object({
    /** Optional author-supplied identifier; also the `after_followup_id` link target. */
    id: followupIdSchema.optional(),
    order: z.number().int().nonnegative(),
    type: followupTypeSchema,
    threshold: z.number().int().positive(),
    timeUnit: followupTimeUnitSchema,
    instructions: z.string().optional(),
    excludedWeekDays: z.array(z.number().int().min(0).max(6)).optional(),
    excludeHolidays: z.boolean().optional(),
    excludedTimeStart: z.string().optional(),
    excludedTimeEnd: z.string().optional(),
    delivery_mode: z.enum(['agent_generated', 'direct']).optional(),
    direct_text: z.string().optional(),
    template_whatsapp_id: z.string().nullable().optional(),
    outside_window_policy: z.enum(['route_to_team', 'skip']).optional(),
    include_tools: z.boolean().optional(),
    /**
     * The `before_event` followup whose fire anchors this chain — its `id`, in
     * the same status. One name, one meaning, contract and schedule column
     * alike: "the followup whose fire anchors this chain". Required on
     * `inactivity_after_before_event`, forbidden on every other type; both are
     * enforced per status, where the sibling followups are visible.
     */
    after_followup_id: followupIdSchema.optional(),
  })
  .passthrough()
  .superRefine((followup, ctx) => {
    if (followup.delivery_mode === 'direct' && !followup.direct_text?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['direct_text'],
        message: 'direct_text is required and must be non-empty when delivery_mode="direct"',
      });
    }
  });

// =============================================================================
// OUTCOME SCHEMA — closing outcomes on the terminal status
// =============================================================================

// Generous cap; a terminal status typically has 2–5 outcomes (resolved/canceled/…).
const MAX_OUTCOMES_PER_STATUS = 20;

/**
 * A closing outcome offered when a conversation enters the terminal status.
 * `slug` is the stored, analytics-facing identifier (ClickHouse `data.meta.outcome`);
 * `name` is the display label. `from_statuses`, when present, restricts the
 * source statuses from which the outcome may be selected.
 */
export const kanbanOutcomeSchema = z
  .object({
    slug: kanbanStatusSlugSchema,
    name: z
      .string()
      .min(1, 'name is required')
      .max(100, 'name must be 100 characters or fewer')
      .refine((v) => visibleLength(v) > 0, 'name must not be blank'),
    color: z.string().optional(),
    /** Source status slugs that may select this outcome. Omission means any
     * source; an empty allowlist is invalid. Cross-references are validated by
     * `kanbanStatusesArraySchema`, which can see all sibling statuses. */
    from_statuses: z
      .array(kanbanStatusSlugSchema)
      .min(1, 'from_statuses cannot be empty — omit it to allow any source status')
      .optional(),
  })
  .passthrough();

/**
 * Return the terminal outcomes eligible for a source status.
 *
 * A missing, unknown, or same-as-target source is treated as unrestricted for
 * compatibility with conversations whose stored status predates the current
 * hub configuration. Genuine known transitions apply each outcome's optional
 * source allowlist. Configured ordering is preserved.
 */
export function getEligibleKanbanOutcomes<T extends { from_statuses?: readonly string[] }>(
  outcomes: readonly T[] | null | undefined,
  sourceStatus: string | null | undefined,
  targetStatus: string,
  knownStatusSlugs: Iterable<string>,
): T[] {
  const configured = Array.isArray(outcomes) ? outcomes : [];
  if (
    !sourceStatus ||
    sourceStatus === targetStatus ||
    !new Set(knownStatusSlugs).has(sourceStatus)
  ) {
    return [...configured];
  }
  return configured.filter(
    (outcome) => outcome.from_statuses === undefined || outcome.from_statuses.includes(sourceStatus),
  );
}

// =============================================================================
// PER-STATUS SCHEMA
// =============================================================================

/**
 * Mutually-exclusive boolean flag pairs. Order within each pair is purely for
 * the error path: the issue is anchored on the second flag (the one the user
 * most recently toggled in typical UI flows).
 */
const EXCLUSIVE_FLAG_PAIRS = [
  ['isInitialStatus', 'triggersAgentResponse'],
  ['isInitialStatus', 'allowsAgentUpdate'],
  ['isInitialStatus', 'isTerminalStatus'],
  ['isInitialStatus', 'isSchedulingStatus'],
  ['triggersAgentResponse', 'allowsAgentUpdate'],
  ['triggersAgentResponse', 'isTerminalStatus'],
] as const;

/**
 * Validate `after_followup_id` against its sibling followups.
 *
 * Same-status is the correct scope: a linked chain is cancelled and rebuilt per
 * status, so a link that crossed statuses could never be resolved at fire time.
 * A cross-status reference therefore lands here as a dangling one.
 *
 * The duplicate-target case is a hard error rather than a runtime
 * de-duplication because nothing at runtime can cover it: two triggers sharing
 * an id and firing farther apart than the chain's own duration means the first
 * chain has already completed, so the cancel-before-arm guard finds nothing
 * pending and the whole sequence delivers a second time.
 */
function validateFollowupLinks(
  followups: readonly FollowupInput[],
  label: string,
  ctx: z.RefinementCtx,
): void {
  const linkTargets = new Set<string>();

  followups.forEach((followup, i) => {
    const ref = followup?.after_followup_id;
    const path = ['followups', i, 'after_followup_id'];

    if (followup?.type !== 'inactivity_after_before_event') {
      if (ref !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: `kanban status ${label}: after_followup_id is only valid on inactivity_after_before_event followups (this one is ${followup?.type})`,
        });
      }
      return;
    }

    if (ref === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path,
        message: `kanban status ${label}: inactivity_after_before_event requires after_followup_id naming the id of a before_event followup in the same status`,
      });
      return;
    }

    linkTargets.add(ref);

    const matches = followups.filter((candidate) => candidate?.id === ref);
    if (matches.length > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path,
        message: `kanban status ${label}: after_followup_id "${ref}" matches ${matches.length} followups — a link must name exactly one`,
      });
      return;
    }

    const target = matches[0];
    if (!target) {
      // An id-less before_event is the likely intended target, so say so:
      // "unknown id" alone sends the author looking for a typo.
      const hasIdlessBeforeEvent = followups.some(
        (candidate) => candidate?.type === 'before_event' && candidate?.id === undefined,
      );
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path,
        message: hasIdlessBeforeEvent
          ? `kanban status ${label}: after_followup_id "${ref}" matches no followup — a link target must declare an explicit id, and this status has before_event followups without one`
          : `kanban status ${label}: after_followup_id "${ref}" matches no followup in this status`,
      });
      return;
    }

    if (target.type !== 'before_event') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path,
        message: `kanban status ${label}: after_followup_id "${ref}" points at a ${target.type} followup — only before_event followups can anchor a chain`,
      });
    }
  });

  if (linkTargets.size > MAX_LINKED_FOLLOWUP_TARGETS_PER_STATUS) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['followups'],
      message: `kanban status ${label}: at most ${MAX_LINKED_FOLLOWUP_TARGETS_PER_STATUS} distinct after_followup_id targets per status (found ${linkTargets.size}) — each one rebuilds a chain on every inbound message`,
    });
  }
}

export const kanbanStatusSchema = z
  .object({
    slug: kanbanStatusSlugSchema,
    name: z
      .string()
      .min(1, 'name is required')
      .max(100, 'name must be 100 characters or fewer')
      .refine((v) => visibleLength(v) > 0, 'name must not be blank'),
    order: z.number().int().nonnegative().optional(),
    color: z.string().optional(),
    isInitialStatus: z.boolean().optional(),
    triggersAgentResponse: z.boolean().optional(),
    allowsAgentUpdate: z.boolean().optional(),
    isTerminalStatus: z.boolean().optional(),
    isSchedulingStatus: z.boolean().optional(),
    eventName: z.string().optional(),
    /** Minutes after the scheduled event date at which the conversation is
     *  flagged `event_due` for the support team. Only meaningful on a
     *  scheduling status (enforced in the superRefine below). 0 = flag at the
     *  event's start instant. */
    event_due_delay_minutes: z.number().int().nonnegative().optional(),
    additional_instructions: z.string().optional(),
    additional_context_schema: z
      .record(z.unknown())
      // `withinJsonLength`, not a bare `JSON.stringify(...).length`: the schema is
      // arbitrary caller JSON, and a value too deep to re-serialize would throw
      // RangeError here rather than being rejected (see its doc).
      .refine(
        (s) => withinJsonLength(s, MAX_ADDITIONAL_CONTEXT_SCHEMA_BYTES),
        `additional_context_schema must be at most ${MAX_ADDITIONAL_CONTEXT_SCHEMA_BYTES} bytes when JSON-serialized`,
      )
      .optional(),
    followups: z
      .array(followupSchema)
      .max(MAX_FOLLOWUPS_PER_STATUS, `followups must have at most ${MAX_FOLLOWUPS_PER_STATUS} entries`)
      .optional(),
    /** Slugs this status may transition to. `undefined` = unrestricted (any
     *  next status). Empty array `[]` is rejected — author must use
     *  `isTerminalStatus: true` to express "no outbound transitions". */
    allowed_next_statuses: z
      .array(z.string())
      .min(1, 'allowed_next_statuses cannot be empty — use isTerminalStatus: true for no outbound transitions')
      .optional(),
    /** Optional presentational grouping — the slug of a hub `lanes[]` entry this
     *  status belongs to. `undefined` = ungrouped. Purely a board-filter/organization
     *  concept; it does NOT constrain transitions (those live in `allowed_next_statuses`)
     *  and is not exposed to agents. The cross-reference invariant (must point at a
     *  declared lane; the initial status must not have one) is enforced at the hub
     *  level via `validateLaneReferences` — this schema only sees the status array. */
    lane_slug: kanbanStatusSlugSchema.optional(),
    /** Closing outcomes offered on the terminal transition. Only valid when
     *  `isTerminalStatus === true` (enforced in the superRefine below). */
    outcomes: z
      .array(kanbanOutcomeSchema)
      .max(MAX_OUTCOMES_PER_STATUS, `outcomes must have at most ${MAX_OUTCOMES_PER_STATUS} entries`)
      .optional(),
  })
  .passthrough()
  .superRefine((status, ctx) => {
    const label = status.name ? `"${status.name}"` : status.slug ? `"${status.slug}"` : '<unnamed>';

    for (const [a, b] of EXCLUSIVE_FLAG_PAIRS) {
      if (status[a] === true && status[b] === true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [b],
          message: `kanban status ${label}: ${a} and ${b} cannot both be true`,
        });
      }
    }

    if (status.isSchedulingStatus === true && !status.eventName?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eventName'],
        message: `kanban status ${label}: eventName is required when isSchedulingStatus=true`,
      });
    }

    if (status.event_due_delay_minutes !== undefined && status.isSchedulingStatus !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['event_due_delay_minutes'],
        message: `kanban status ${label}: event_due_delay_minutes requires isSchedulingStatus=true`,
      });
    }

    if (Array.isArray(status.followups) && status.isSchedulingStatus !== true) {
      const offending = status.followups.find(
        (f) => isEventRelativeFollowupType(f?.type),
      );
      if (offending) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['followups'],
          message: `kanban status ${label}: ${offending.type} followups require isSchedulingStatus=true on the parent status`,
        });
      }
    }

    if (Array.isArray(status.followups)) {
      validateFollowupLinks(status.followups, label, ctx);
    }

    // Reserved-name collision: `additional_data` is the magic dump placeholder.
    if (status.additional_context_schema && typeof status.additional_context_schema === 'object') {
      const schema = status.additional_context_schema as Record<string, unknown>;
      const props = schema.properties;
      if (props && typeof props === 'object' && RESERVED_TEMPLATE_DUMP_NAME in (props as object)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['additional_context_schema', 'properties', RESERVED_TEMPLATE_DUMP_NAME],
          message: `kanban status ${label}: "${RESERVED_TEMPLATE_DUMP_NAME}" is a reserved template placeholder name and cannot be used as a top-level schema property`,
        });
      }
    }

    // Outcomes are a closing-disposition concept — only meaningful on the terminal
    // status, where entering it closes the conversation. Reject them elsewhere.
    if (Array.isArray(status.outcomes) && status.outcomes.length > 0) {
      if (status.isTerminalStatus !== true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['outcomes'],
          message: `kanban status ${label}: outcomes are only allowed on the terminal status (set isTerminalStatus=true)`,
        });
      }
      // Unique outcome slugs — the slug is the stored analytics identifier.
      const seen = new Map<string, number[]>();
      status.outcomes.forEach((o, j) => {
        if (!o?.slug) return;
        const arr = seen.get(o.slug) ?? [];
        arr.push(j);
        seen.set(o.slug, arr);
      });
      for (const [slug, indices] of seen) {
        if (indices.length > 1) {
          for (const j of indices) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['outcomes', j, 'slug'],
              message: `kanban status ${label}: duplicate outcome slug "${slug}"`,
            });
          }
        }
      }
    }
  });

// =============================================================================
// ARRAY SCHEMA — cross-status invariants
// =============================================================================

export const kanbanStatusesArraySchema = z
  .array(kanbanStatusSchema)
  .max(MAX_KANBAN_STATUSES, `kanban_statuses must have at most ${MAX_KANBAN_STATUSES} entries`)
  .superRefine((statuses, ctx) => {
    if (statuses.length === 0) return;

    const initialIndices = statuses
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => s.isInitialStatus === true);

    if (initialIndices.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [],
        message: 'kanban_statuses: exactly one status must have isInitialStatus=true (found 0)',
      });
    } else if (initialIndices.length > 1) {
      const slugs = initialIndices.map(({ s }) => s.slug).join(', ');
      for (const { i } of initialIndices) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [i, 'isInitialStatus'],
          message: `kanban_statuses: only one status may have isInitialStatus=true (found ${initialIndices.length}: ${slugs})`,
        });
      }
    }

    // At most one terminal status per hub (unlike initial, zero is allowed — hubs
    // may close only via the team Close button or inactivity auto-close). One
    // terminal keeps the board's close sink and the `outcomes` dimension singular.
    const terminalIndices = statuses
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => s.isTerminalStatus === true);
    if (terminalIndices.length > 1) {
      const slugs = terminalIndices.map(({ s }) => s.slug).join(', ');
      for (const { i } of terminalIndices) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [i, 'isTerminalStatus'],
          message: `kanban_statuses: at most one status may have isTerminalStatus=true (found ${terminalIndices.length}: ${slugs})`,
        });
      }
    }

    // Unique slugs (slugs are the runtime identifier — duplicates corrupt routing).
    const seenSlugs = new Map<string, number[]>();
    statuses.forEach((s, i) => {
      const key = s.slug;
      if (!key) return;
      const arr = seenSlugs.get(key) ?? [];
      arr.push(i);
      seenSlugs.set(key, arr);
    });
    for (const [slug, indices] of seenSlugs) {
      if (indices.length > 1) {
        for (const i of indices) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [i, 'slug'],
            message: `kanban_statuses: duplicate slug "${slug}"`,
          });
        }
      }
    }

    // allowed_next_statuses entries must reference sibling slugs.
    const validSlugs = new Set(statuses.map((s) => s.slug).filter(Boolean));
    statuses.forEach((s, i) => {
      if (!Array.isArray(s.allowed_next_statuses)) return;
      s.allowed_next_statuses.forEach((target, j) => {
        if (typeof target !== 'string' || target.length === 0) return;
        if (!validSlugs.has(target)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [i, 'allowed_next_statuses', j],
            message: `kanban_statuses[${i}] (slug "${s.slug}"): allowed_next_statuses references unknown slug "${target}"`,
          });
        }
      });
    });

    // Outcome source allowlists must reference declared, non-terminal statuses.
    statuses.forEach((s, i) => {
      if (!Array.isArray(s.outcomes)) return;
      s.outcomes.forEach((outcome, outcomeIndex) => {
        if (!Array.isArray(outcome.from_statuses)) return;
        outcome.from_statuses.forEach((source, sourceIndex) => {
          if (!validSlugs.has(source)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [i, 'outcomes', outcomeIndex, 'from_statuses', sourceIndex],
              message: `kanban_statuses[${i}] outcome "${outcome.slug}": from_statuses references unknown slug "${source}"`,
            });
          } else if (source === s.slug) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [i, 'outcomes', outcomeIndex, 'from_statuses', sourceIndex],
              message: `kanban_statuses[${i}] outcome "${outcome.slug}": terminal status "${s.slug}" cannot be its own source`,
            });
          }
        });
      });
    });
  });

// =============================================================================
// LANE SCHEMA — presentational grouping of kanban statuses
// =============================================================================

/**
 * A lane is an optional, hub-level grouping that statuses link to via `lane_slug`,
 * surfaced as a board filter. Presentational only — no transition semantics, not
 * agent-visible. Lives inside the hub config (alongside `kanban_statuses`), NOT as a
 * separate hub-as-code entity like eval journeys.
 */
export const laneSchema = z
  .object({
    slug: kanbanStatusSlugSchema,
    name: z
      .string()
      .min(1, 'name is required')
      .max(100, 'name must be 100 characters or fewer')
      .refine((v) => visibleLength(v) > 0, 'name must not be blank'),
    color: z.string().optional(),
    order: z.number().int().nonnegative().optional(),
  })
  .passthrough();

export const lanesArraySchema = z
  .array(laneSchema)
  .max(MAX_LANES, `lanes must have at most ${MAX_LANES} entries`)
  .superRefine((lanes, ctx) => {
    // Unique slugs — slug is the reference target for `status.lane_slug`.
    const seen = new Map<string, number[]>();
    lanes.forEach((l, i) => {
      if (!l.slug) return;
      const arr = seen.get(l.slug) ?? [];
      arr.push(i);
      seen.set(l.slug, arr);
    });
    for (const [slug, indices] of seen) {
      if (indices.length > 1) {
        for (const i of indices) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i, 'slug'], message: `lanes: duplicate slug "${slug}"` });
        }
      }
    }
  });

export interface LaneReferenceIssue {
  /** Path relative to the `kanban_statuses` array — callers prepend `'kanban_statuses'`. */
  path: (string | number)[];
  message: string;
}

/**
 * Cross-field invariant between the hub's `lanes` and its `kanban_statuses`. Lives
 * here (not in `kanbanStatusesArraySchema`) because that schema can't see `lanes`.
 * Called from the hub-level contract `superRefine` (request body) AND the backend
 * handler against the MERGED config — the latter is required because a partial PATCH
 * sends only one of the two arrays, so the request-body check alone can't catch a
 * `lane_slug` orphaned by a lanes-only edit (or vice versa).
 *
 *   2. A status's `lane_slug` (when set) must reference a declared lane.
 *   3. The initial status must NOT have a `lane_slug` (the shared-entry carve-out).
 */
export function validateLaneReferences(
  lanes: LaneInput[] | null | undefined,
  statuses: KanbanStatusInput[] | null | undefined,
): LaneReferenceIssue[] {
  const issues: LaneReferenceIssue[] = [];
  if (!Array.isArray(statuses) || statuses.length === 0) return issues;
  const declared = new Set((Array.isArray(lanes) ? lanes : []).map((l) => l.slug).filter(Boolean));

  statuses.forEach((s, i) => {
    const lane = s.lane_slug;
    if (typeof lane !== 'string' || lane.length === 0) return;
    const label = s.slug ? `"${s.slug}"` : `[${i}]`;
    if (s.isInitialStatus === true) {
      issues.push({
        path: [i, 'lane_slug'],
        message: `kanban status ${label}: the initial status cannot belong to a lane (it is the shared entry point)`,
      });
      return;
    }
    if (!declared.has(lane)) {
      issues.push({
        path: [i, 'lane_slug'],
        message: `kanban status ${label}: lane_slug references unknown lane "${lane}"`,
      });
    }
  });
  return issues;
}

// =============================================================================
// LINT — non-blocking warnings (returned alongside successful saves)
// =============================================================================

export interface KanbanLintWarning {
  code:
    | 'unreachable'
    | 'placeholder_unresolved'
    | 'unused_lane'
    | 'no_eligible_outcome'
    | 'terminal_outcomes_all_restricted'
    | 'duplicate_followup_id';
  slug: string;
  name: string;
  message: string;
}

/**
 * Lint pass for kanban_statuses. Returns warnings that don't block save.
 *
 * - `unreachable`: a non-initial status that no other status lists in its
 *   `allowed_next_statuses`. Conversations cannot reach it.
 * - `placeholder_unresolved`: an `{{path}}` placeholder in `additional_instructions`
 *   that does not match any path in the declared `additional_context_schema`.
 *
 * - `unused_lane`: a declared lane that no status references via `lane_slug`.
 *   (Only emitted when `lanes` is passed.)
 * - `no_eligible_outcome`: a source can transition directly to the terminal
 *   status, but none of its configured outcomes accept that source.
 * - `terminal_outcomes_all_restricted`: a source status that no outcome accepts
 *   AND that `allowed_next_statuses` already bars from reaching the terminal
 *   status. `no_eligible_outcome` skips exactly these, because the transition
 *   gate refuses the edge — but the team Close button is exempt from that gate
 *   when the target is terminal, so it closes from there with no outcome
 *   recorded. Complementary to `no_eligible_outcome`: never both for one status.
 * - `duplicate_followup_id`: two followups in one status share an `id`. A
 *   warning rather than an error because ids only became meaningful with
 *   `after_followup_id`, and the pre-UUID editor minted `followup-${Date.now()}`
 *   — two followups added in the same millisecond collide, and a hub already
 *   holding such a pair must not start failing every PATCH over a field its UI
 *   never showed. A duplicate that is actually LINKED is a hard contract error;
 *   see `validateFollowupLinks`.
 *
 * Run after Zod validation succeeds; surface in the response so authors can
 * see warnings without being blocked mid-edit.
 */
export function lintKanbanStatuses(statuses: KanbanStatusInput[], lanes?: LaneInput[]): KanbanLintWarning[] {
  if (!Array.isArray(statuses) || statuses.length === 0) return [];

  const slugs = statuses.map((s) => s.slug).filter(Boolean);
  if (slugs.length === 0) return [];

  // -- Reachability ---------------------------------------------------------
  const allSlugs = new Set(slugs);
  const inboundFromAny = new Set<string>();
  const inboundExplicit = new Map<string, Set<string>>();

  for (const s of statuses) {
    if (!s.slug) continue;
    if (s.isTerminalStatus === true) continue;
    if (s.allowed_next_statuses === undefined) {
      for (const other of allSlugs) {
        if (other !== s.slug) inboundFromAny.add(other);
      }
    } else {
      for (const target of s.allowed_next_statuses) {
        if (!allSlugs.has(target)) continue;
        const set = inboundExplicit.get(target) ?? new Set<string>();
        set.add(s.slug);
        inboundExplicit.set(target, set);
      }
    }
  }

  const warnings: KanbanLintWarning[] = [];
  for (const s of statuses) {
    if (!s.slug) continue;
    if (s.isInitialStatus === true) continue;
    const hasInbound = inboundFromAny.has(s.slug) || (inboundExplicit.get(s.slug)?.size ?? 0) > 0;
    if (!hasInbound) {
      warnings.push({
        code: 'unreachable',
        slug: s.slug,
        name: s.name,
        message: `kanban status "${s.slug}" is unreachable — no other status transitions to it and it is not isInitialStatus`,
      });
    }
  }

  // -- Source-to-terminal outcome eligibility ------------------------------
  const terminal = statuses.find((status) => status.isTerminalStatus === true);
  if (terminal?.outcomes?.length) {
    // Gated on `outcomes.length > 0` for the same reason the source scan below is:
    // "no outcome accepts this status" is vacuously true of an empty list, which
    // is every hub that configures no outcomes at all.
    for (const source of statuses) {
      if (!source.slug || source.slug === terminal.slug || source.isTerminalStatus === true) continue;
      const canReachTerminal =
        source.allowed_next_statuses === undefined ||
        source.allowed_next_statuses.includes(terminal.slug);
      const eligible = getEligibleKanbanOutcomes(
        terminal.outcomes,
        source.slug,
        terminal.slug,
        allSlugs,
      );
      if (eligible.length > 0) continue;
      if (canReachTerminal) {
        warnings.push({
          code: 'no_eligible_outcome',
          slug: source.slug,
          name: source.name,
          message: `kanban status "${source.slug}" can transition to terminal status "${terminal.slug}", but no terminal outcome accepts it`,
        });
      } else {
        // `no_eligible_outcome` deliberately skips these — the transition gate
        // already refuses the edge, so an agent or a board drag can never reach
        // the terminal status from here. The team Close button CAN: a non-agent
        // caller is exempt from `allowed_next_statuses` when the target is
        // terminal. It then closes with no outcome recorded, silently. Reported
        // per source, like its sibling, so `slug`/`name` name the status the
        // author has to act on. The two are complementary: never both for one
        // status.
        warnings.push({
          code: 'terminal_outcomes_all_restricted',
          slug: source.slug,
          name: source.name,
          message: `no terminal outcome accepts kanban status "${source.slug}" — its allowed_next_statuses bars the transition, but the team Close button is exempt from that gate, so closing from it records NO outcome`,
        });
      }
    }
  }

  // -- Duplicate followup ids ----------------------------------------------
  for (const s of statuses) {
    if (!s.slug || !Array.isArray(s.followups)) continue;
    const counts = new Map<string, number>();
    for (const f of s.followups) {
      if (typeof f?.id !== 'string' || f.id.length === 0) continue;
      counts.set(f.id, (counts.get(f.id) ?? 0) + 1);
    }
    for (const [id, count] of counts) {
      if (count > 1) {
        warnings.push({
          code: 'duplicate_followup_id',
          slug: s.slug,
          name: s.name,
          message: `kanban status "${s.slug}": followup id "${id}" is used by ${count} followups — ids must be unique to be usable as an after_followup_id link target`,
        });
      }
    }
  }

  // -- Placeholder paths vs schema -----------------------------------------
  for (const s of statuses) {
    if (!s.slug || !s.additional_instructions) continue;
    const paths = extractPlaceholderPaths(s.additional_instructions);
    if (paths.length === 0) continue;
    const schema = s.additional_context_schema;
    for (const path of paths) {
      if (path === RESERVED_TEMPLATE_DUMP_NAME) continue;
      if (!schema || !isPathInJsonSchema(schema, path)) {
        warnings.push({
          code: 'placeholder_unresolved',
          slug: s.slug,
          name: s.name,
          message: `kanban status "${s.slug}": template placeholder "{{${path}}}" does not match any path in additional_context_schema`,
        });
      }
    }
  }

  // -- Unused lanes (declared but unreferenced) ----------------------------
  if (Array.isArray(lanes) && lanes.length > 0) {
    const referenced = new Set(statuses.map((s) => s.lane_slug).filter((v): v is string => typeof v === 'string' && v.length > 0));
    for (const lane of lanes) {
      if (!lane.slug || referenced.has(lane.slug)) continue;
      warnings.push({
        code: 'unused_lane',
        slug: lane.slug,
        name: lane.name,
        message: `lane "${lane.slug}" is declared but no kanban status is assigned to it`,
      });
    }
  }

  return warnings;
}

function extractPlaceholderPaths(template: string): string[] {
  const out = new Set<string>();
  for (const match of template.matchAll(PLACEHOLDER_RE)) {
    out.add(match[1]);
  }
  return Array.from(out);
}

/**
 * Lint-side path resolver against a JSON Schema (Draft 2020-12 subset).
 * Walks `properties` for objects and `items` for arrays. Numeric segments
 * after an array indirect into `items`. Refs / oneOf / unknown shapes
 * return false (lint is best-effort; runtime renders empty string anyway).
 *
 * Treats an absent `type` as object-like when `properties` is present
 * (legal per JSON Schema spec — type is not required) and array-like when
 * `items` is present. Without these, schemas authored as raw YAML by CLI/MCP
 * users (who often omit `type`) would false-positive every placeholder.
 */
function isPathInJsonSchema(schema: unknown, path: string): boolean {
  if (typeof schema !== 'object' || schema === null) return false;
  const segments = path.split('.');
  let cur: unknown = schema;
  for (const seg of segments) {
    if (typeof cur !== 'object' || cur === null) return false;
    const obj = cur as Record<string, unknown>;
    const propsObj = obj.properties && typeof obj.properties === 'object' ? (obj.properties as Record<string, unknown>) : null;
    const itemsObj = obj.items && typeof obj.items === 'object' ? (obj.items as Record<string, unknown>) : null;
    const isObjectLike = obj.type === 'object' || (obj.type === undefined && propsObj !== null);
    const isArrayLike = obj.type === 'array' || (obj.type === undefined && itemsObj !== null && propsObj === null);
    if (isObjectLike && propsObj) {
      if (!(seg in propsObj)) return false;
      cur = propsObj[seg];
    } else if (isArrayLike && itemsObj) {
      if (!/^\d+$/.test(seg)) return false;
      cur = itemsObj;
    } else {
      return false;
    }
  }
  return true;
}

// =============================================================================
// INFERRED TYPES
// =============================================================================

export type FollowupInput = z.infer<typeof followupSchema>;
export type KanbanOutcomeInput = z.infer<typeof kanbanOutcomeSchema>;
export type KanbanStatusInput = z.infer<typeof kanbanStatusSchema>;
export type LaneInput = z.infer<typeof laneSchema>;
